import React, { useEffect, forwardRef, useImperativeHandle } from 'react';

const days = Array.from({ length: 31 }, (_, i) => i + 1);
const months = [
  { value: 'January', label: 'January' },
  { value: 'February', label: 'February' },
  { value: 'March', label: 'March' },
  { value: 'April', label: 'April' },
  { value: 'May', label: 'May' },
  { value: 'June', label: 'June' },
  { value: 'July', label: 'July' },
  { value: 'August', label: 'August' },
  { value: 'September', label: 'September' },
  { value: 'October', label: 'October' },
  { value: 'November', label: 'November' },
  { value: 'December', label: 'December' },
];
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 100 }, (_, i) => currentYear - 18 - i);

const dobValidationRjV1 = (day, month, year) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const monthNum = monthNames.indexOf(month);
  if (monthNum === -1) return false;
  const d = parseInt(day, 10);
  const y = parseInt(year, 10);
  const date = new Date(y, monthNum, d);
  return (
    date.getFullYear() === y &&
    date.getMonth() === monthNum &&
    date.getDate() === d
  );
};

const ChevronDown = () => (
  <svg
    className="position-absolute pointer-events-none text-blue-900 icon-sm"
    style={{ right: '0.5rem', top: '50%', transform: 'translateY(-50%)' }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth="3"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const InfoBanner = () => (
  <div className="d-flex align-items-center gap-4 bg-blue-50-half rounded-4 p-3 mt-5 border border-blue-100 shadow-sm animate-pop-in">
    <div className="flex-shrink-0 bg-white rounded-3 shadow-sm d-flex align-items-center justify-content-center p-2 w-12 h-12">
      <svg viewBox="0 0 100 100" className="w-100 h-100">
        <circle cx="50" cy="35" r="15" fill="#cfd8dc" />
        <rect x="30" y="55" width="40" height="30" rx="10" fill="#2196f3" />
        <circle cx="20" cy="50" r="10" fill="#4caf50" />
        <path d="M15 50 L18 53 L25 45" stroke="white" strokeWidth="3" fill="none" />
      </svg>
    </div>
    <p className="fw-semibold text-gray-600 leading-tight mb-0 fs-12">
      Your personal information is vital for verifying your identity and finance
      agreement, allowing us to assess your PCP claim accurately.
    </p>
  </div>
);

const DateOfBirth = forwardRef(({
  slideChange,
  slideView,
  previousSlideChange,
  register,
  validationMsg,
  trigger,
  clearErrors,
  setError,
  getValues,
  watch,
  setIsNextActive,
}, ref) => {
  const DobDay   = watch('DobDay');
  const DobMonth = watch('DobMonth');
  const DobYear  = watch('DobYear');

  // Reactive date validation
  useEffect(() => {
    if (!DobDay || !DobMonth || !DobYear) return;
    if (!dobValidationRjV1(DobDay, DobMonth, DobYear)) {
      setError('DobYear', { type: 'manual', message: 'Please enter a valid date of birth.' });
    } else {
      clearErrors('DobYear');
    }
  }, [DobDay, DobMonth, DobYear]);

  const isButtonActive =
    DobDay &&
    DobMonth &&
    DobYear &&
    dobValidationRjV1(DobDay, DobMonth, DobYear);

  // Keep parent sticky button in sync
  useEffect(() => {
    if (slideView !== 'show') return;
    setIsNextActive(!!isButtonActive);
  }, [DobDay, DobMonth, DobYear, slideView]);

  const handleNext = async () => {
    const isValid = await trigger(['DobDay', 'DobMonth', 'DobYear']);
    if (!isValid) return;
    const { DobDay: d, DobMonth: m, DobYear: y } = getValues();
    if (!dobValidationRjV1(d, m, y)) return;
    slideChange({ target: { name: 'next2' } });
  };

  // Expose handleNext to parent via ref
  useImperativeHandle(ref, () => ({ handleNext }));

  return (
    <div className={`slide ${slideView === 'show' ? 'active' : 'd-none'}`} id="slide-2">
      <h3 className="fs-4 fw-bold tracking-tight text-gray-900 animate-stagger-up delay-200">
        Date Of Birth
      </h3>
      <p className="fw-medium text-gray-700 mb-5 animate-stagger-up delay-200 fs-15">
        We need your date of birth to confirm your identity
      </p>

      <div className="mx-auto animate-stagger-up delay-400">
        {/* Day */}
        <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 mb-4 transition-colors rounded-10">
          <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
            Select Day <span className="text-red-500">*</span>
          </legend>
          <div className="position-relative">
            <select
              name="DobDay"
              ref={register({ required: 'Please select a day.' })}
              className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2 cursor-pointer"
              style={{ outline: 'none' }}
              onChange={() => clearErrors('DobDay')}
            >
              <option value="">Select Day</option>
              {days.map((d) => (
                <option key={d} value={String(d).padStart(2, '0')}>
                  {d}
                </option>
              ))}
            </select>
            <ChevronDown />
          </div>
        </fieldset>
        {validationMsg?.DobDay && (
          <p className="error_msg">{validationMsg.DobDay.message}</p>
        )}

        {/* Month */}
        <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 mb-4 transition-colors rounded-10">
          <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
            Select Month <span className="text-red-500">*</span>
          </legend>
          <div className="position-relative">
            <select
              name="DobMonth"
              ref={register({ required: 'Please select a month.' })}
              className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2 cursor-pointer"
              style={{ outline: 'none' }}
              onChange={() => clearErrors('DobMonth')}
            >
              <option value="">Select Month</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <ChevronDown />
          </div>
        </fieldset>
        {validationMsg?.DobMonth && (
          <p className="error_msg">{validationMsg.DobMonth.message}</p>
        )}

        {/* Year */}
        <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 mb-4 transition-colors rounded-10">
          <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
            Select Year <span className="text-red-500">*</span>
          </legend>
          <div className="position-relative">
            <select
              name="DobYear"
              ref={register({ required: 'Please select a year.' })}
              className="w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2 cursor-pointer"
              style={{ outline: 'none' }}
            >
              <option value="">Select Year</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <ChevronDown />
          </div>
        </fieldset>
        {validationMsg?.DobYear && (
          <p className="error_msg">{validationMsg.DobYear.message}</p>
        )}

        <InfoBanner />

        {/* Spacer so sticky bar never overlaps content */}
        <div style={{ height: '5rem' }} />
      </div>
    </div>
  );
});

export default DateOfBirth;